import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Package, Search, Filter, Download, Plus, MapPin, Boxes, AlertTriangle } from 'lucide-react';
import InventoryControl from './InventoryControl';
import { WarehousePDFGenerator } from '../../utils/warehousePDFGenerator';

const WarehouseInventory = () => {
  const [activeView, setActiveView] = useState('inventory'); // inventory, control
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  
  const inventoryItems = [
    { id: 1, sku: 'ELEC-001', name: 'Laptop Dell XPS 15', category: 'Electronics', quantity: 125, location: 'Zone A-12', status: 'in-stock', value: 125000 },
    { id: 2, sku: 'FURN-045', name: 'Office Chair Ergonomic', category: 'Furniture', quantity: 89, location: 'Zone B-08', status: 'in-stock', value: 44500 },
    { id: 3, sku: 'APPL-089', name: 'Air Conditioner 2 Ton', category: 'Appliances', quantity: 45, location: 'Zone C-15', status: 'low-stock', value: 67500 },
    { id: 4, sku: 'OFF-123', name: 'Printer HP LaserJet', category: 'Office Supplies', quantity: 67, location: 'Zone A-05', status: 'in-stock', value: 33500 },
    { id: 5, sku: 'RAW-234', name: 'Steel Sheets 10mm', category: 'Raw Materials', quantity: 1200, location: 'Zone D-20', status: 'in-stock', value: 240000 },
    { id: 6, sku: 'ELEC-156', name: 'Monitor 27" 4K', category: 'Electronics', quantity: 12, location: 'Zone A-10', status: 'critical', value: 24000 },
  ];

  return (
    <div className="space-y-6">
      {/* View Toggle */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
          {[
            { id: 'inventory', label: 'Inventory List' },
            { id: 'control', label: 'Inventory Control' }
          ].map((view) => (
            <button
              key={view.id}
              onClick={() => setActiveView(view.id)}
              className={`flex-1 px-4 py-2 rounded-md transition-colors ${
                activeView === view.id
                  ? 'bg-white text-orange-700 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {view.label}
            </button>
          ))}
        </div>
      </div>

      {/* Inventory Control View */}
      {activeView === 'control' && (
        <InventoryControl />
      )}

      {/* Standard Inventory View */}
      {activeView === 'inventory' && (
        <div>
      {/* Filters Panel */}
      {showFilters && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">All Categories</option>
                {[...new Set(inventoryItems.map(item => item.category))].map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">All Status</option>
                <option value="in-stock">In Stock</option>
                <option value="low-stock">Low Stock</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm">
                <option value="">All Locations</option>
                {[...new Set(inventoryItems.map(item => item.location.split('-')[0]))].map(loc => (
                  <option key={loc} value={loc}>Zone {loc}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Header Actions */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex-1 w-full sm:max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by SKU, name, or location..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
            <Filter className="w-4 h-4" />
            Filter
          </button>
          <button className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2">
            <Download className="w-4 h-4" />
            Export
          </button>
          <button className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Item
          </button>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Item Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {inventoryItems
                .filter(item => {
                  if (!searchQuery) return true;
                  const query = searchQuery.toLowerCase();
                  return (
                    item.sku.toLowerCase().includes(query) ||
                    item.name.toLowerCase().includes(query) ||
                    item.location.toLowerCase().includes(query)
                  );
                })
                .map((item) => (
                <motion.tr 
                  key={item.id}
                  whileHover={{ backgroundColor: '#f9fafb' }}
                  className="cursor-pointer"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-semibold text-gray-900">{item.sku}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-sm text-gray-900">{item.name}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">{item.category}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">{item.quantity}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-1 text-sm text-gray-600">
                      <MapPin className="w-4 h-4" />
                      {item.location}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      item.status === 'in-stock' ? 'bg-green-100 text-green-800' :
                      item.status === 'low-stock' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm font-medium text-gray-900">SAR {item.value.toLocaleString()}</span>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </div>
      )}
    </div>
  );
};

export default WarehouseInventory;
